/**
 * TinyMCE FontAwesome Icon Picker Plugin
 *
 * Provides a dialog-based icon picker for inserting FontAwesome icons into TinyMCE.
 * Supports search, category filtering, and style filtering.
 */

import iconData from '../data/fontawesome-icons.json';

export class FontAwesomeIconPickerPlugin {
    constructor(editor) {
        this.editor = editor;
        this.iconData = iconData;
        this.selectedIcon = null;
        this.searchTimeout = null;
        this.currentDialogApi = null;
    }

    /**
     * Initialize the plugin
     */
    init() {
        console.log('🎨 Initializing FontAwesome Icon Picker Plugin for editor:', this.editor.id);

        // Load icon data
        this.iconData = iconData;
        console.log(`📦 Loaded ${this.iconData.icons.length} FontAwesome icons`);

        // Inject CSS styles
        this.injectStyles();

        // Register toolbar button
        this.registerToolbarButton();

        // Register menu item
        this.registerMenuItem();

        console.log('✅ FontAwesome Icon Picker Plugin initialized');
    }

    /**
     * Register the toolbar button
     */
    registerToolbarButton() {
        // Register custom FontAwesome logo icon
        this.editor.ui.registry.addIcon('fontawesome', '<svg width="24" height="24"><path d="M4 2v20h1.5V13.5c.8-.3 1.6-.5 2.5-.5 2.5 0 3.5 1.5 7 1.5 2 0 3.2-.6 5.5-1.5V3C18.2 4 17 4.5 15 4.5c-3.5 0-4.5-1.5-7-1.5-1 0-1.7.1-2.5.4V2H4Zm1.5 2.9c.8-.3 1.6-.4 2.5-.4 2.5 0 3.5 1.5 7 1.5 1.5 0 2.5-.3 4.5-1v7.5c-1.8.7-2.8 1-4.5 1-3.5 0-4.5-1.5-7-1.5-.9 0-1.7.1-2.5.4V4.9Z" fill-rule="evenodd"/></svg>');

        this.editor.ui.registry.addButton('iconpicker', {
            icon: 'fontawesome',
            tooltip: 'Insert FontAwesome icon',
            onAction: () => {
                this.openDialog().catch(err => {
                    console.error('Error opening icon picker:', err);
                });
            }
        });
    }

    /**
     * Register menu item
     */
    registerMenuItem() {
        this.editor.ui.registry.addMenuItem('iconpicker', {
            icon: 'non-breaking',
            text: 'FontAwesome Icon',
            onAction: () => {
                this.openDialog().catch(err => {
                    console.error('Error opening icon picker:', err);
                });
            }
        });
    }

    /**
     * Inject CSS styles for the icon picker
     */
    injectStyles() {
        // Inject FontAwesome CSS if not already present
        const faLinkId = 'fontawesome-cdn-css';
        if (!document.getElementById(faLinkId)) {
            const faLink = document.createElement('link');
            faLink.id = faLinkId;
            faLink.rel = 'stylesheet';
            faLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
            document.head.appendChild(faLink);
            console.log('✅ FontAwesome CSS injected for icon picker');
        }

        // Inject custom icon picker styles
        const styleId = 'fontawesome-icon-picker-styles';
        if (document.getElementById(styleId)) return;

        const styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
            /* Compact TinyMCE dialog spacing */
            .tox-dialog__body-content {
                padding: 16px !important;
            }

            /* The icon picker manages its own scrolling inside
               .fa-icon-picker-scroll, so its dialog body must not scroll too.
               Scoped with :has() so every other dialog keeps TinyMCE's own
               overflow (a tall section form scrolls instead of overflowing
               past the footer). */
            .tox-dialog__body-content:has(.fa-icon-picker-wrapper) {
                overflow: visible !important;
            }

            .tox-dialog__body-content .tox-form__group {
                margin-bottom: 8px !important;
            }

            .tox-dialog__body-content .tox-form__group:last-child {
                margin-bottom: 0 !important;
            }

            /* CRITICAL: Prevent search input from flex-growing */
            .tox-dialog__body-content .tox-form-group--maximize {
                flex-grow: 0 !important;
                flex-shrink: 0 !important;
            }

            /* Ensure footer with buttons is always visible */
            .tox-dialog__footer {
                position: sticky !important;
                bottom: 0 !important;
                background: white !important;
                border-top: 1px solid #e5e7eb !important;
                z-index: 10 !important;
            }

            /* Remove TinyMCE's default label spacing */
            .tox-label {
                margin-bottom: 4px !important;
            }

            /* TinyMCE grid (Category/Style row) - reduce spacing */
            .tox-form__grid {
                margin-bottom: 8px !important;
            }

            .tox-form__grid--2col > div {
                padding-right: 8px !important;
            }

            .tox-form__grid--2col > div:last-child {
                padding-right: 0 !important;
            }

            /* HTMLPanel wrapper - remove all padding/margin */
            .tox-dialog__body-content .tox-form__group .tox-form__controls-h-stack {
                padding: 0 !important;
            }

            /* Main wrapper - flex column, fixed total height */
            .fa-icon-picker-wrapper {
                display: flex !important;
                flex-direction: column !important;
                height: 400px !important;
                gap: 12px !important;
            }

            /* Scrollable icon grid area - takes remaining space */
            .fa-icon-picker-scroll {
                flex: 1 1 0 !important;
                min-height: 0 !important;
                overflow-y: auto !important;
            }

            /* Icon grid - natural height within scroll container */
            .fa-icon-picker-grid {
                display: grid !important;
                grid-template-columns: repeat(8, 1fr) !important;
                gap: 10px !important;
                padding: 16px !important;
                background: #ffffff !important;
                border: 1px solid #d1d5db !important;
                border-radius: 6px !important;
                margin: 0 !important;
            }

            .fa-icon-picker-btn {
                width: 52px;
                height: 52px;
                border: 2px solid #e5e7eb;
                border-radius: 6px;
                background: #f9fafb;
                cursor: pointer;
                transition: all 0.15s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
            }

            .fa-icon-picker-btn:hover {
                border-color: #3b82f6;
                background: #eff6ff;
                transform: translateY(-2px);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            .fa-icon-picker-btn.selected {
                border-color: #2563eb;
                background: #dbeafe;
                box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
            }

            .fa-icon-picker-btn:active {
                transform: translateY(0);
            }

            .fa-icon-picker-btn i {
                font-size: 24px;
                color: #374151;
                font-family: "Font Awesome 6 Free", "Font Awesome 6 Brands" !important;
                font-weight: 900 !important;
                font-style: normal !important;
                font-variant: normal !important;
                text-rendering: auto !important;
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
                display: inline-block !important;
            }

            .fa-icon-picker-btn i.fa-brands,
            .fa-icon-picker-btn i.fab {
                font-family: "Font Awesome 6 Brands" !important;
                font-weight: 400 !important;
            }

            .fa-icon-picker-btn:hover i {
                color: #1f2937;
            }

            /* Status message - fixed height */
            .fa-icon-picker-wrapper .fa-icon-picker-status {
                flex: 0 0 auto !important;
                padding: 12px 16px !important;
                margin: 0 !important;
                text-align: center !important;
                color: #6b7280 !important;
                font-size: 13px !important;
                background: #f9fafb !important;
                border-radius: 6px !important;
                border: 1px solid #e5e7eb !important;
                display: none !important;
                line-height: 1.5 !important;
            }

            .fa-icon-picker-wrapper .fa-icon-picker-status.visible {
                display: block !important;
            }

            /* Preview - fixed height */
            .fa-icon-picker-wrapper .fa-icon-picker-preview {
                flex: 0 0 auto !important;
                padding: 16px 20px !important;
                margin: 0 !important;
                background: #f9fafb !important;
                border: 2px solid #e5e7eb !important;
                border-radius: 8px !important;
                display: none !important;
                align-items: center !important;
                gap: 18px !important;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
            }

            .fa-icon-picker-wrapper .fa-icon-picker-preview[style*="display: flex"] {
                display: flex !important;
            }

            .fa-icon-picker-wrapper .fa-icon-picker-preview > i {
                font-size: 40px !important;
                color: #111827 !important;
                min-width: 56px !important;
                width: 56px !important;
                height: 56px !important;
                text-align: center !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                background: #ffffff !important;
                border-radius: 8px !important;
                border: 2px solid #e5e7eb !important;
                font-family: "Font Awesome 6 Free" !important;
                font-weight: 900 !important;
                font-style: normal !important;
                font-variant: normal !important;
                text-rendering: auto !important;
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
            }

            .fa-icon-picker-wrapper .fa-icon-picker-preview > i.fab,
            .fa-icon-picker-wrapper .fa-icon-picker-preview > i.fa-brands {
                font-family: "Font Awesome 6 Brands" !important;
                font-weight: 400 !important;
            }

            .fa-icon-picker-wrapper .fa-icon-picker-preview-info {
                flex: 1 !important;
                min-width: 0 !important;
            }

            .fa-icon-picker-wrapper .fa-icon-picker-preview-name {
                font-weight: 600 !important;
                font-size: 15px !important;
                color: #111827 !important;
                margin-bottom: 6px !important;
                line-height: 1.4 !important;
            }

            .fa-icon-picker-wrapper .fa-icon-picker-preview-code {
                font-family: 'Monaco', 'Menlo', 'Courier New', monospace !important;
                font-size: 12px !important;
                color: #059669 !important;
                background: #f0fdf4 !important;
                border: 1px solid #bbf7d0 !important;
                padding: 6px 10px !important;
                border-radius: 4px !important;
                display: inline-block !important;
                user-select: all !important;
                line-height: 1.4 !important;
            }

            .fa-icon-picker-empty {
                grid-column: 1 / -1;
                padding: 40px 20px;
                text-align: center;
                color: #9ca3af;
                font-size: 14px;
                background: #fafafa;
                border-radius: 6px;
            }

            .fa-icon-picker-loading {
                grid-column: 1 / -1;
                padding: 40px 20px;
                text-align: center;
                color: #6b7280;
                font-size: 14px;
            }

            /* Tooltip on hover */
            .fa-icon-picker-btn::after {
                content: attr(data-tooltip);
                position: absolute;
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 4px 8px;
                border-radius: 3px;
                font-size: 11px;
                white-space: nowrap;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s;
                margin-bottom: 4px;
                z-index: 1000;
            }

            .fa-icon-picker-btn:hover::after {
                opacity: 1;
            }
        `;
        document.head.appendChild(styleEl);
    }

    /**
     * Ensure FontAwesome is loaded
     */
    async ensureFontAwesomeLoaded() {
        // Check if FontAwesome is already loaded by testing if icons render
        const testIcon = document.createElement('i');
        testIcon.className = 'fas fa-home';
        testIcon.style.position = 'absolute';
        testIcon.style.visibility = 'hidden';
        document.body.appendChild(testIcon);

        // Wait a bit for fonts to load
        await new Promise(resolve => setTimeout(resolve, 100));

        // Check if the icon has actual width (meaning font is loaded)
        const hasWidth = testIcon.offsetWidth > 0;
        document.body.removeChild(testIcon);

        if (!hasWidth) {
            console.log('⏳ Waiting for FontAwesome to load...');
            // Wait for fonts to be ready
            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
            } else {
                // Fallback: wait a bit longer
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        console.log('✅ FontAwesome is ready');
    }

    /**
     * Open the icon picker dialog
     */
    async openDialog() {
        console.log('🖼️  Opening FontAwesome Icon Picker...');

        // Ensure FontAwesome is loaded
        await this.ensureFontAwesomeLoaded();

        // Reset selection
        this.selectedIcon = null;

        const self = this;

        // Build category dropdown items
        const categoryItems = [
            { text: 'All Categories', value: 'all' },
            ...this.iconData.categories.map(cat => ({ text: cat, value: cat }))
        ];

        // Build style dropdown items
        const styleItems = [
            { text: 'All Styles', value: 'all' },
            { text: 'Solid', value: 'solid' },
            { text: 'Regular', value: 'regular' },
            { text: 'Brands', value: 'brands' }
        ];

        // Open dialog
        const dialogConfig = {
            title: 'FontAwesome Icon Picker',
            size: 'large',
            body: {
                type: 'panel',
                items: [
                    {
                        type: 'input',
                        name: 'search',
                        placeholder: 'Search icons by name or keyword...',
                        maximized: true
                    },
                    {
                        type: 'grid',
                        columns: 2,
                        items: [
                            {
                                type: 'selectbox',
                                name: 'category',
                                label: 'Category',
                                items: categoryItems
                            },
                            {
                                type: 'selectbox',
                                name: 'style',
                                label: 'Style',
                                items: styleItems
                            }
                        ]
                    },
                    {
                        type: 'htmlpanel',
                        html: '<div class="fa-icon-picker-wrapper">' +
                              '<div class="fa-icon-picker-scroll">' +
                              '<div class="fa-icon-picker-grid" id="fa-icon-grid"></div>' +
                              '</div>' +
                              '<div class="fa-icon-picker-status" id="fa-icon-status"></div>' +
                              '<div class="fa-icon-picker-preview" id="fa-icon-preview" style="display: none;"></div>' +
                              '</div>'
                    }
                ]
            },
            buttons: [
                {
                    type: 'cancel',
                    name: 'cancel',
                    text: 'Cancel'
                },
                {
                    type: 'submit',
                    name: 'submit',
                    text: 'Insert Icon',
                    primary: true,
                    enabled: false
                }
            ],
            initialData: {
                search: '',
                category: 'all',
                style: 'all'
            },
            onChange: (api, details) => {
                // Debounced search
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }

                this.searchTimeout = setTimeout(() => {
                    const data = api.getData();
                    this.filterAndRenderIcons(data.search, data.category, data.style);
                }, 300);
            },
            onSubmit: (api) => {
                if (this.selectedIcon) {
                    this.insertIcon(this.selectedIcon.name, this.selectedIcon.prefix);
                    api.close();
                }
            }
        };

        const dialogApi = this.editor.windowManager.open(dialogConfig);
        this.currentDialogApi = dialogApi;

        // Initial render after dialog opens
        setTimeout(() => {
            this.filterAndRenderIcons('', 'all', 'all');
        }, 100);
    }

    /**
     * Filter and render icons based on search and filter criteria
     */
    filterAndRenderIcons(searchTerm, category, style) {
        const filteredIcons = this.filterIcons(searchTerm, category, style);
        this.renderIconGrid(filteredIcons);
    }

    /**
     * Filter icons based on search term, category, and style
     */
    filterIcons(searchTerm, category, style) {
        const term = (searchTerm || '').toLowerCase().trim();

        return this.iconData.icons.filter(icon => {
            // Search in name, label, and keywords
            const matchesSearch = !term ||
                icon.name.includes(term) ||
                icon.label.toLowerCase().includes(term) ||
                icon.search.some(kw => kw.toLowerCase().includes(term));

            // Filter by category
            const matchesCategory = !category || category === 'all' ||
                icon.categories.includes(category);

            // Filter by style
            const matchesStyle = !style || style === 'all' ||
                icon.styles.includes(style);

            return matchesSearch && matchesCategory && matchesStyle;
        });
    }

    /**
     * Render icon grid
     */
    renderIconGrid(icons) {
        const gridEl = document.getElementById('fa-icon-grid');
        if (!gridEl) {
            console.error('❌ Icon grid element not found');
            return;
        }

        if (icons.length === 0) {
            gridEl.innerHTML = '<div class="fa-icon-picker-empty">No icons found matching your criteria.</div>';
            return;
        }

        // Render icons (limit to first 200 for performance, add "load more" later if needed)
        const iconsToRender = icons.slice(0, 200);

        console.log(`📊 Rendering ${iconsToRender.length} icons`);
        console.log(`📊 First icon:`, iconsToRender[0]);

        gridEl.innerHTML = iconsToRender.map(icon => {
            // Use first available style
            const style = icon.styles[0];
            const prefix = this.iconData.styles[style].prefix;

            // Debug: log first few icons
            if (iconsToRender.indexOf(icon) < 3) {
                console.log(`📊 Icon: ${icon.name}, Style: ${style}, Prefix: ${prefix}, Class: ${prefix} fa-${icon.name}`);
            }

            return `
                <button type="button"
                        class="fa-icon-picker-btn"
                        data-icon-name="${icon.name}"
                        data-icon-prefix="${prefix}"
                        data-icon-label="${icon.label}"
                        data-tooltip="${icon.label}">
                    <i class="${prefix} fa-${icon.name}" aria-hidden="true"></i>
                </button>
            `;
        }).join('');

        // Update status message
        const statusEl = document.getElementById('fa-icon-status');
        if (statusEl) {
            if (icons.length > 200) {
                statusEl.innerHTML = `📊 Showing first 200 of ${icons.length} icons. Refine your search for more specific results.`;
                statusEl.className = 'fa-icon-picker-status visible';
            } else {
                statusEl.className = 'fa-icon-picker-status';
            }
        }

        // Add click handlers
        const buttons = gridEl.querySelectorAll('.fa-icon-picker-btn');
        buttons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const iconName = btn.getAttribute('data-icon-name');
                const iconPrefix = btn.getAttribute('data-icon-prefix');
                const iconLabel = btn.getAttribute('data-icon-label');

                // Find full icon data
                const icon = this.iconData.icons.find(i => i.name === iconName);
                if (icon) {
                    // Update selection
                    this.selectedIcon = {
                        name: iconName,
                        prefix: iconPrefix,
                        label: iconLabel,
                        icon: icon
                    };

                    // Update UI
                    this.showPreview(this.selectedIcon);
                    this.updateSelectedState(iconName);

                    // Enable submit button
                    if (this.currentDialogApi) {
                        this.currentDialogApi.setEnabled('submit', true);
                    }
                }
            });
        });

        // Setup keyboard navigation
        this.setupKeyboardNavigation(gridEl);
    }

    /**
     * Update selected state in grid
     */
    updateSelectedState(selectedIconName) {
        const buttons = document.querySelectorAll('.fa-icon-picker-btn');
        buttons.forEach(btn => {
            if (btn.getAttribute('data-icon-name') === selectedIconName) {
                btn.classList.add('selected');
            } else {
                btn.classList.remove('selected');
            }
        });
    }

    /**
     * Show preview of selected icon
     */
    showPreview(iconData) {
        const previewEl = document.getElementById('fa-icon-preview');
        if (!previewEl) return;

        const htmlCode = `&lt;i class="${iconData.prefix} fa-${iconData.name}"&gt;&lt;/i&gt;`;

        previewEl.innerHTML = `
            <i class="${iconData.prefix} fa-${iconData.name}"></i>
            <div class="fa-icon-picker-preview-info">
                <div class="fa-icon-picker-preview-name">${iconData.label}</div>
                <div class="fa-icon-picker-preview-code">${htmlCode}</div>
            </div>
        `;
        previewEl.style.display = 'flex';
    }

    /**
     * Setup keyboard navigation for icon grid
     */
    setupKeyboardNavigation(gridEl) {
        gridEl.addEventListener('keydown', (e) => {
            const buttons = Array.from(gridEl.querySelectorAll('.fa-icon-picker-btn'));
            const currentIndex = buttons.indexOf(document.activeElement);

            if (currentIndex === -1) return;

            let nextIndex = currentIndex;

            switch (e.key) {
                case 'ArrowRight':
                    nextIndex = Math.min(currentIndex + 1, buttons.length - 1);
                    break;
                case 'ArrowLeft':
                    nextIndex = Math.max(currentIndex - 1, 0);
                    break;
                case 'ArrowDown':
                    nextIndex = Math.min(currentIndex + 8, buttons.length - 1);
                    break;
                case 'ArrowUp':
                    nextIndex = Math.max(currentIndex - 8, 0);
                    break;
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    buttons[currentIndex].click();
                    return;
                default:
                    return;
            }

            if (nextIndex !== currentIndex) {
                e.preventDefault();
                buttons[nextIndex].focus();
            }
        });
    }

    /**
     * Insert icon into editor
     */
    insertIcon(iconName, stylePrefix) {
        const iconHtml = `<i class="${stylePrefix} fa-${iconName}"></i>&nbsp;`;

        // Focus editor first
        this.editor.focus();

        // Insert content
        this.editor.insertContent(iconHtml);

        console.log(`✅ Inserted icon: ${iconHtml}`);
    }
}
